import React, { useMemo } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import { injectIntl } from "@ai360/core/intl";
import moment from "moment";

import { AgvanceAPI, AppHelpers, FieldAPI, SkyAPI, UserAPI, apiUrl } from "@ai360/core";
import SearchAgvanceField from "~/action-panel/components/common/search-agvance-field";
import { actions } from "~/action-panel/components/field-module/";
import {
    clearAgvanceCustomerList,
    fetchAgvanceCustomer,
    fetchAgvanceCustomerWithNameSearch,
    resetAgvanceCustomers,
} from "~/admin/setup/customer/data/actions";
import { IrrigatedListOptions } from "../../../../sagas";
import { fetchedDropdownData } from "~/action-panel/components/field-module/actions";
import {
    getFieldModuleDropdowns,
    getAgvanceFieldList,
    getAgvanceFieldClassificationList,
    getAgvanceLocationsList,
    getAgvanceOrgLevelList,
    getErrorCodes,
    getAgvanceFarmSplits,
    getAgvanceFieldData,
    getFarmNames,
    getFetchingFieldInfo,
} from "~/action-panel/components/field-module/selectors";
import { adminData, GUID, NAME, PICKLIST_GUID, VALUE } from "~/admin/data";
import { AgvanceUtils } from "~/admin/setup/customer/agvance-utils";
import {
    Bucket,
    BucketHeader,
    Checkbox,
    DateInput,
    DialogBox,
    DialogBoxFooterType,
    NoLink,
    NumericInput,
    SelectInput,
    SelectInputMulti,
    TextArea,
    TextInput,
} from "~/core";
import { fetchDropdownData } from "~/core/dropdowns/actions";
import { TrashcanIcon } from "~/core/icons";
import { selectors as picklistSelectors } from "~/core/picklist";
import {
    PICKLIST_CROP_PURPOSE,
    PICKLIST_IRRIGATION_TYPE,
    PICKLIST_INSURANCE_POLICY_TYPE,
    PICKLIST_LAND_OWNERSHIP_TYPE,
    PICKLIST_PRODUCT_PARENT_TYPE,
    getPickListCode,
} from "~/core/picklist/picklist-names";
import { selectors as unitSelectors } from "~/core/units";
import { UNIT_YIELD, getUnitCode } from "~/core/units/unit-names";
import { withApiResult } from "~/hocs";
import { messages as globalMessages } from "~/i18n-messages";
import { getTheUserPersonalityId, getUser } from "~/login/selectors";
import { optionRendererWithCustomTitle } from "~/action-panel/components/common/validation-utils";

import messages from "./i18n-messages";

import "./field-information-detail.css";
import { IFieldModel } from "~/action-panel/components/field-module/models";
import { logFirebaseEvent } from "~/utils/firebase";
import FieldBoundaryTransfer from "../field-boundary-transfer/field-boundary-transfer";
import SearchAgvanceCustomer from "~/admin/setup/component/search-agvance-customer";
import * as selectors from "~/admin/setup/customer/data/selectors";
import {
    ICropDropdownOption,
    isCropUnlinked,
    toCropDropdownOptions,
    useCropList,
} from "~/admin/setup/customer/use-crop-list";
import { IS_SELECTABLE, computeIsSelectable } from "~/admin/setup/customer/use-product-list";

export const pickLists = {
    [PICKLIST_CROP_PURPOSE]: getPickListCode(PICKLIST_CROP_PURPOSE),
    [PICKLIST_IRRIGATION_TYPE]: getPickListCode(PICKLIST_IRRIGATION_TYPE),
    [PICKLIST_INSURANCE_POLICY_TYPE]: getPickListCode(PICKLIST_INSURANCE_POLICY_TYPE),
    [PICKLIST_LAND_OWNERSHIP_TYPE]: getPickListCode(PICKLIST_LAND_OWNERSHIP_TYPE),
    [PICKLIST_PRODUCT_PARENT_TYPE]: getPickListCode(PICKLIST_PRODUCT_PARENT_TYPE),
};
export const units = {
    [UNIT_YIELD]: getUnitCode(UNIT_YIELD),
};

enum BillSplitLevel {
    Field,
    Farm,
    Default,
    Empty,
}

const DROPDOWN_BRANDORGANIZATION = "DROPDOWN_BRANDORGANIZATION";
const DROPDOWN_VARIETYHYBRID = "DROPDOWN_VARIETYHYBRID";

export const dropdowns = {
    [DROPDOWN_BRANDORGANIZATION]: {
        url: apiUrl("AgBytes/GetBrandOrganizationCropList"),
        model: "",
    },
    [DROPDOWN_VARIETYHYBRID]: {
        url: apiUrl("AgBytes/GetVarietyHybridFilterList"),
        model: { cropId: "", brandOrganization: "" },
    },
};

interface IProps {
    activeField: IFieldModel;
    agvanceCustomerList: AgvanceAPI.ICustomer[];
    agvanceFieldData: FieldAPI.IAgvanceField;
    agvanceFarmSplits: AgvanceAPI.IFarmResult[];
    agvanceFieldList: AgvanceAPI.IFieldResult[];
    agvanceOrgList: AgvanceAPI.ICustomerResult[];
    agvanceFieldClassificationList: FieldAPI.IAgvClassification[];
    agvanceLocationId: string;
    agvanceLocationsList: SkyAPI.ILocationResponse[];
    apiError: any;
    clearAgvanceCustomerList: () => void;
    clearAgvanceFarmSplits: () => void;
    clearAgvanceFieldList: () => void;
    clearAgvanceFieldClassificationList: () => void;
    clearAgvanceLocationsList: () => void;
    clearFarmNames: () => void;
    clearFieldsToMove: () => void;
    cropData: ICropDropdownOption[];
    customerGuid: string;
    customerSearchIsLoading: boolean;
    editFieldGuid: string;
    errorCodes: any[];
    farmNames: string[];
    onFetchAgvanceCustomer: (payload: any) => void;
    onFetchAgvanceCustomerWithNameSearch: (payload: any) => void;
    fetchAgvanceFarmSplits: (customerGuid: string) => void;
    fetchAgvanceFieldList: (customerGuid: string) => void;
    fetchAgvanceFieldClassificationList: (orgLevelGuid: string) => void;
    fetchAgvanceLocationsList: (payload: {
        customerGuid: string;
        agvanceFieldOrgLevelGuid: string;
    }) => void;
    fetchAgvanceOrgLevelList: (customerGuid: string) => void;
    fetchFarmNames: (payload: { active: boolean | null; customerGuid: string }) => void;
    fetchingFieldInfo: boolean;
    hasActiveInactive: boolean;
    isAdditionalAgvanceCustomerSearch: boolean;
    isAgvanceCustomerListOutOfBound: boolean;
    intl: any;
    onInsuranceCropUnlinkedChange?: (hasUnlinkedCrop: boolean) => void;
    personalityId: number;
    refreshDropdowns: (dropdowns: any) => void;
    resetAgvanceCustomerList: () => void;
    updateFieldModel: (model: any) => void;
    userInfo: UserAPI.IUser;
}

interface IState {
    agvanceCustomer: string;
    agvanceCustomerList: AgvanceAPI.ICustomer[];
    agvanceFieldAcres: any;
    billSplits: FieldAPI.IBillSplit[];
    billSplitIndexToDelete: number;
    billSplitLevel: BillSplitLevel;
    customerFarms: Set<string>;
    errorCodes: any[];
    farmForBillingSplits: AgvanceAPI.IFarmResult;
    insurance: any;
    showBillSplitsDeleteDialog: boolean;
    showCertOrganicChangeDialog: boolean;
    showFarmSplitConfirmDialog: boolean;
    newCertOrganicValue: boolean;
    shouldGetAgvanceCustomer: boolean;
    shouldGetAgvanceCustomersForBilling: boolean;
    [key: string]: any;
}

class FieldInformationDetail extends React.Component<IProps, IState> {
    private cropListOptions: any[];
    private cropLocalOptions: any[];
    private brandListOptions: any[];
    private varietyListOptions: any[];
    private cropPurposeListOptions: any[];
    private cropPurposeLocalOptions: any[];
    private irrigationTypeListOptions: any[];
    private insurancePolicyTypeListOptions: any[];
    private leaseTypeListOptions: any[];
    private yieldUnitListOptions: any[];
    private farmListOptions: any[];
    private irrigatedListOptions: any[];
    private timeoutId: any;
    private lastReportedInsuranceCropUnlinked = false;

    /// ----------------------------------------- ///

    constructor(props: IProps) {
        super(props);

        this.state = {
            agvanceCustomerList: [],
            agvanceFieldAcres: null,
            farmForBillingSplits: null,
            insurance: {
                cropGuid: "",
                cropName: "",
                cropPurposeGuid: "",
                cropPurposeName: "",
                yieldUnitGuid: "",
                yieldUnitName: "",
                countyTYield: null,
                averageYield: null,
                calculatedYield: null,
                fieldGuid: "",
                fieldCropYieldGuid: "",
            },
            billSplits: [],
            billSplitIndexToDelete: null,
            billSplitLevel: BillSplitLevel.Empty,
            multiSelectIndex: 0,
            errorCodes: [],
            agvanceCustomer: "",
            showBillSplitsDeleteDialog: false,
            showCertOrganicChangeDialog: false,
            showFarmSplitConfirmDialog: false,
            newCertOrganicValue: undefined,
            customerFarms: new Set(),
            shouldGetAgvanceCustomer: false,
            shouldGetAgvanceCustomersForBilling: false,
        };
        this.setupLists(props, null);
    }

    componentDidMount() {
        if (this.props.customerGuid && this.props.activeField) {
            this.agvanceInit(this.props.customerGuid);
            this.props.fetchFarmNames({ active: true, customerGuid: this.props.customerGuid });
        }
        this.reportInsuranceCropUnlinkedIfChanged();
    }

    // Insurance crop guid lives in this component's own local state (state.insurance.cropGuid),
    // invisible to field-information.jsx's Save gate otherwise -- report only the one boolean it needs.
    private reportInsuranceCropUnlinkedIfChanged() {
        const isUnlinked = isCropUnlinked(this.state.insurance.cropGuid, this.props.cropData);
        if (isUnlinked !== this.lastReportedInsuranceCropUnlinked) {
            this.lastReportedInsuranceCropUnlinked = isUnlinked;
            this.props.onInsuranceCropUnlinkedChange?.(isUnlinked);
        }
    }

    componentDidUpdate(prevProps: IProps) {
        this.setupLists(this.props, prevProps);
        if (this.props.farmNames !== prevProps.farmNames) {
            this.setState({
                customerFarms: new Set(this.props.farmNames),
            });
        }
        if (this.props.agvanceOrgList !== prevProps.agvanceOrgList) {
            if (this.props.agvanceOrgList?.length === 1) {
                this.updateFieldForAgvanceCustomers(
                    this.props.agvanceOrgList[0],
                    this.props.activeField,
                    this.keepSavedAgvanceLocation(this.props.activeField)
                );
            }
            this.setState({
                shouldGetAgvanceCustomer: true,
                shouldGetAgvanceCustomersForBilling: true,
            });
        }
        if (
            this.state.shouldGetAgvanceCustomersForBilling &&
            this.props.agvanceCustomerList?.length === 0 &&
            this.props.agvanceOrgList?.length > 0
        ) {
            // Update bill splits for single Agvance customer
            const selectedAgvanceOrg = this.props.agvanceOrgList.filter(
                ({ isPrimary }) => isPrimary
            );
            if (
                this.state.billSplitLevel === BillSplitLevel.Empty &&
                selectedAgvanceOrg?.length &&
                !this.showAdditionalAgvanceCustomer(this.props)
            ) {
                const names = this.props.agvanceOrgList[0].agvanceGrowerName.split(" ");
                const defaultBillSplit = this.getNewBillSplit(
                    this.props.agvanceOrgList[0].agvanceGrowerId,
                    names[0],
                    names.length > 1 ? names[1] : ""
                );
                this.updateSplitInFieldModel([defaultBillSplit], null, null);
                this.setBillSplitsDetails([defaultBillSplit], null, null, BillSplitLevel.Default);
            }
            // Fetch Customer
            if (!this.disableBillSplitsEditing()) {
                this.props.onFetchAgvanceCustomer(
                    this.props.agvanceOrgList.map((org) => org.orgLevelGuid)
                );
            }
            this.setState({
                shouldGetAgvanceCustomersForBilling: false,
            });
        }
        if (
            this.state.shouldGetAgvanceCustomer &&
            !this.props.fetchingFieldInfo &&
            !this.state.agvanceCustomer &&
            this.showAdditionalAgvanceCustomer(this.props)
        ) {
            this.setState({
                agvanceCustomer: this.getAgvanceCustomer(
                    this.props.agvanceOrgList,
                    this.props.activeField
                ),
                shouldGetAgvanceCustomer: false,
            });
        }
        if (
            this.props.agvanceFieldData != null &&
            this.props.agvanceFieldData !== prevProps.agvanceFieldData
        ) {
            this.setState({
                agvanceFieldAcres: this.props.agvanceFieldData.acres,
            });
        }
        if (this.props.activeField?.classifications !== prevProps.activeField?.classifications) {
            this.setState({
                multiSelectIndex: this.state.multiSelectIndex + 1,
            });
        }

        if (
            this.props.agvanceFieldData?.splitInfo?.length !==
                prevProps.agvanceFieldData?.splitInfo?.length &&
            this.props.agvanceFieldData?.splitInfo.length > 0
        ) {
            this.setupBillingSplitFromAgvance(this.props.agvanceFieldData, BillSplitLevel.Field);
        }

        this.resetRestrictedAgvanceLocation();
        this.reportInsuranceCropUnlinkedIfChanged();
    }

    componentWillUnmount() {
        if (this.props.agvanceFieldList?.length) {
            this.props.clearAgvanceFieldList();
        }
        this.props.clearAgvanceFarmSplits();
        this.props.clearFarmNames();
        this.props.clearAgvanceFieldClassificationList();
        this.props.clearAgvanceLocationsList();
        this.props.clearAgvanceCustomerList();
        this.props.clearFieldsToMove();
    }

    private agvanceInit(customerGuid: string) {
        const {
            activeField,
            fetchAgvanceFarmSplits,
            fetchAgvanceFieldList,
            fetchAgvanceLocationsList,
            fetchAgvanceOrgLevelList,
            userInfo,
        } = this.props;

        if (this.isAgvanceConnected()) {
            fetchAgvanceFieldList(customerGuid);
            fetchAgvanceOrgLevelList(customerGuid);
            if (userInfo?.role?.agvanceLocation) {
                fetchAgvanceLocationsList({
                    customerGuid,
                    agvanceFieldOrgLevelGuid: activeField.agvanceFieldOrgLevelGuid,
                });
                this.updateFieldModel("agvanceLocationId", "(NONE)");
            }
            fetchAgvanceFarmSplits(customerGuid);
        }
    }

    private setupBillingSplitFromAgvance(agvanceFieldData, level) {
        const { updateFieldModel, activeField } = this.props;
        const newFieldModel = {
            ...activeField,
            s2SplitArrangement: agvanceFieldData?.s2SplitArrangement,
            s3SplitArrangement: agvanceFieldData?.s3SplitArrangement,
            billingSplits: agvanceFieldData?.splitInfo,
        };
        updateFieldModel(newFieldModel);
        this.setState({
            billSplits: agvanceFieldData?.splitInfo,
            billSplitLevel: level,
        });
    }

    private updateFieldForAgvanceCustomers(
        agvanceOrgDetails: any,
        activeField: any,
        modifiedFieldModel: any = {}
    ) {
        const { updateFieldModel, fetchAgvanceFieldClassificationList } = this.props;
        const newFieldModel = {
            ...activeField,
            agvanceFieldOrgLevelGuid: agvanceOrgDetails.orgLevelGuid,
            agvanceCustomerGuid: agvanceOrgDetails.agvanceCustomerGuid,
            agvanceLocationId: agvanceOrgDetails.locationId,
            ...modifiedFieldModel,
        };
        if (AgvanceUtils.isAgvanceConnected(this.props.personalityId)) {
            fetchAgvanceFieldClassificationList(agvanceOrgDetails.orgLevelGuid);
        }

        updateFieldModel(newFieldModel);
    }

    private keepSavedAgvanceLocation(activeField: any): { agvanceLocationId?: string } {
        return activeField?.fieldGuid ? { agvanceLocationId: activeField.agvanceLocationId } : {};
    }

    private getSelectableOptions(options: any[], guid: string, label: string, selected: any) {
        if (options) {
            return options.reduce((result, option) => {
                result.push({
                    value: option[guid],
                    label: option[label],
                    selected: option[guid] === selected,
                    activeYn: option[adminData.PROPS_ACTIVE_YN],
                });
                return result;
            }, []);
        }
        return [];
    }

    // Like getSelectableOptions, but for crop options specifically -- stamps isSelectable/getToolTip
    // from isAgvanceLinked so the two crop SelectInputs below can hide/mark unlinked crops.
    private getCropSelectableOptions(options: ICropDropdownOption[], selected: any) {
        const { formatMessage } = this.props.intl;
        if (!options) {
            return [];
        }
        return options.map((option) => ({
            value: option.guid,
            label: option.name,
            selected: option.guid === selected,
            activeYn: option.activeYn,
            isSelectable: computeIsSelectable(option.activeYn, option.isAgvanceLinked),
            getToolTip:
                option.isAgvanceLinked === false
                    ? () => formatMessage(globalMessages.cropNotLinkedInAgvance)
                    : undefined,
        }));
    }

    private selectValue(options: any[], value: any) {
        return options.map((option) => {
            return {
                value: option.value,
                label: option.label,
                selected: option.value === value,
                activeYn: option.activeYn,
            };
        });
    }

    private clearSelection(options: any[]) {
        return options.map((option) => {
            return {
                value: option.value,
                label: option.label,
                selected: false,
                activeYn: option.activeYn,
            };
        });
    }

    private getLabelByValue(options: any[], value: any) {
        for (let i = 0; i < options.length; i++) {
            const option = options[i];
            if (option.value === value) {
                return option.label;
            }
        }
    }

    private refreshCropDropdownLists(cropGuid: string, brandOrganizationGuid: string) {
        const refreshDropdowns = {
            [DROPDOWN_BRANDORGANIZATION]: {
                url: apiUrl("AgBytes/GetBrandOrganizationCropList"),
                model: cropGuid,
            },
            [DROPDOWN_VARIETYHYBRID]: {
                url: apiUrl("AgBytes/GetVarietyHybridFilterList"),
                model: {
                    cropId: cropGuid,
                    brandOrganization: brandOrganizationGuid,
                },
            },
        };
        this.props.refreshDropdowns(refreshDropdowns);
    }

    /// Set up lists ///
    private setupLists(props: IProps, oldProps: IProps) {
        const isInit = !oldProps;

        if (isInit || props.cropData !== oldProps.cropData) {
            this.cropListOptions = this.getCropSelectableOptions(
                props.cropData,
                props.activeField.cropGuid
            );
            this.cropLocalOptions = this.getCropSelectableOptions(
                props.cropData,
                this.state.insurance.cropGuid
            );
            props.activeField.fieldCropYieldList?.forEach((cropYield) => {
                cropYield.cropName = this.getLabelByValue(
                    this.cropLocalOptions,
                    cropYield.cropGuid
                );
            });
        }
        if (isInit || props[DROPDOWN_BRANDORGANIZATION] !== oldProps[DROPDOWN_BRANDORGANIZATION]) {
            this.brandListOptions = this.getSelectableOptions(
                props[DROPDOWN_BRANDORGANIZATION],
                GUID,
                NAME,
                props.activeField.brandOrganizationGuid
            );
        }
        if (isInit || props[DROPDOWN_VARIETYHYBRID] !== oldProps[DROPDOWN_VARIETYHYBRID]) {
            this.varietyListOptions = this.getSelectableOptions(
                props[DROPDOWN_VARIETYHYBRID],
                GUID,
                NAME,
                props.activeField.varietyHybridGuid
            );
        }
        if (isInit || props[PICKLIST_CROP_PURPOSE] !== oldProps[PICKLIST_CROP_PURPOSE]) {
            this.cropPurposeListOptions = this.getSelectableOptions(
                props[PICKLIST_CROP_PURPOSE],
                PICKLIST_GUID,
                VALUE,
                props.activeField.cropPurposeGuid
            );
            this.cropPurposeLocalOptions = this.getSelectableOptions(
                props[PICKLIST_CROP_PURPOSE],
                PICKLIST_GUID,
                VALUE,
                this.state.insurance.cropPurposeGuid
            );
            props.activeField.fieldCropYieldList?.forEach((cropYield) => {
                cropYield.cropPurposeName = this.getLabelByValue(
                    this.cropPurposeLocalOptions,
                    cropYield.cropPurposeGuid
                );
            });
        }
        if (isInit || props[PICKLIST_IRRIGATION_TYPE] !== oldProps[PICKLIST_IRRIGATION_TYPE]) {
            this.irrigationTypeListOptions = this.getSelectableOptions(
                props[PICKLIST_IRRIGATION_TYPE],
                PICKLIST_GUID,
                VALUE,
                props.activeField.irrigationTypeGuid
            );
        }
        if (
            isInit ||
            props[PICKLIST_INSURANCE_POLICY_TYPE] !== oldProps[PICKLIST_INSURANCE_POLICY_TYPE]
        ) {
            this.insurancePolicyTypeListOptions = this.getSelectableOptions(
                props[PICKLIST_INSURANCE_POLICY_TYPE],
                PICKLIST_GUID,
                VALUE,
                props.activeField.insurancePolicyTypeGuid
            );
        }
        if (
            isInit ||
            props[PICKLIST_LAND_OWNERSHIP_TYPE] !== oldProps[PICKLIST_LAND_OWNERSHIP_TYPE]
        ) {
            this.leaseTypeListOptions = this.getSelectableOptions(
                props[PICKLIST_LAND_OWNERSHIP_TYPE],
                PICKLIST_GUID,
                VALUE,
                props.activeField.landOwnershipTypeGuid
            );
        }
        if (isInit || props[UNIT_YIELD] !== oldProps[UNIT_YIELD]) {
            this.yieldUnitListOptions = this.getSelectableOptions(
                props[UNIT_YIELD],
                GUID,
                NAME,
                this.state.insurance.yieldUnitGuid
            );
            props.activeField.fieldCropYieldList?.forEach((cropYield) => {
                cropYield.yieldUnitName = this.getLabelByValue(
                    this.yieldUnitListOptions,
                    cropYield.yieldUnitGuid
                );
            });
        }

        if (
            isInit ||
            props.activeField.fieldCropYieldList !== oldProps.activeField.fieldCropYieldList
        ) {
            props.activeField.fieldCropYieldList?.forEach((cropYield) => {
                cropYield.cropName = this.getLabelByValue(
                    this.cropLocalOptions,
                    cropYield.cropGuid
                );
                cropYield.cropPurposeName = this.getLabelByValue(
                    this.cropPurposeLocalOptions,
                    cropYield.cropPurposeGuid
                );
                cropYield.yieldUnitName = this.getLabelByValue(
                    this.yieldUnitListOptions,
                    cropYield.yieldUnitGuid
                );
            });
        }
        if (isInit || this.farmListOptions?.length !== this.state.customerFarms.size) {
            this.setUpFarmListOptions();
        }

        if (isInit || props.activeField.irrigated !== oldProps.activeField.irrigated) {
            this.setUpIrrigatedListOptions();
        }
        if (isInit || props.activeField.cropGuid !== oldProps.activeField.cropGuid) {
            this.cropListOptions = this.selectValue(
                this.cropListOptions,
                props.activeField.cropGuid
            );
        }
        if (
            isInit ||
            props.activeField.brandOrganizationGuid !== oldProps.activeField.brandOrganizationGuid
        ) {
            this.brandListOptions = this.selectValue(
                this.brandListOptions,
                props.activeField.brandOrganizationGuid
            );
        }
        if (
            isInit ||
            props.activeField.varietyHybridGuid !== oldProps.activeField.varietyHybridGuid
        ) {
            this.varietyListOptions = this.selectValue(
                this.varietyListOptions,
                props.activeField.varietyHybridGuid
            );
        }
        if (isInit || props.activeField.cropPurposeGuid !== oldProps.activeField.cropPurposeGuid) {
            this.cropPurposeListOptions = this.selectValue(
                this.cropPurposeListOptions,
                props.activeField.cropPurposeGuid
            );
        }
        if (
            isInit ||
            props.activeField.irrigationTypeGuid !== oldProps.activeField.irrigationTypeGuid
        ) {
            this.irrigationTypeListOptions = this.selectValue(
                this.irrigationTypeListOptions,
                props.activeField.irrigationTypeGuid
            );
        }
        if (
            isInit ||
            props.activeField.insurancePolicyTypeGuid !==
                oldProps.activeField.insurancePolicyTypeGuid
        ) {
            this.insurancePolicyTypeListOptions = this.selectValue(
                this.insurancePolicyTypeListOptions,
                props.activeField.insurancePolicyTypeGuid
            );
        }
        if (
            isInit ||
            props.activeField.landOwnershipTypeGuid !== oldProps.activeField.landOwnershipTypeGuid
        ) {
            this.leaseTypeListOptions = this.selectValue(
                this.leaseTypeListOptions,
                props.activeField.landOwnershipTypeGuid
            );
        }
    }

    private setUpFarmListOptions() {
        this.farmListOptions = [...this.state.customerFarms].map((farmName) => ({
            value: farmName,
            label: farmName,
        }));
    }

    private setUpIrrigatedListOptions() {
        this.irrigatedListOptions = IrrigatedListOptions;
    }
    ////////////////////

    /// Event Handlers ///
    private updateFieldModel(propertyName: string, newValue: any) {
        // Takes a property name (possibly nested, e.g. fieldBoundary.watershedName) and updates the field model with
        // the new value.
        const newFieldModel = { ...this.props.activeField };
        let modelName = "";
        let propName = "";

        if (propertyName.indexOf(".") !== -1) {
            modelName = propertyName.split(".")[0];
            propName = propertyName.split(".")[1];
        }

        if (modelName !== "") {
            newFieldModel[modelName][propName] = newValue;
        } else {
            if (propertyName === "cropGuid") {
                newFieldModel.cropPurposeGuid = "";
                newFieldModel.brandOrganizationGuid = "";
                newFieldModel.varietyHybridGuid = "";
                this.cropPurposeListOptions = this.getSelectableOptions(
                    this.props[PICKLIST_CROP_PURPOSE],
                    PICKLIST_GUID,
                    VALUE,
                    null
                );
                this.brandListOptions = [];
                this.varietyListOptions = [];
                if (newValue) {
                    this.refreshCropDropdownLists(newValue, "");
                }
            } else if (propertyName === "brandOrganizationGuid") {
                newFieldModel.varietyHybridGuid = "";
                this.varietyListOptions = [];
                this.refreshCropDropdownLists(newFieldModel.cropGuid, newValue);
            } else if (propertyName === "irrigated" && !newValue) {
                newFieldModel.irrigationTypeGuid = "";
            } else if (propertyName === "lockBoundary") {
                newFieldModel.lockBoundaryModifiedByGuid = this.props.userInfo.userGuid;
                newFieldModel.lockBoundary = newValue;
            }
            newFieldModel[propertyName] = newValue;
        }
        this.props.updateFieldModel(newFieldModel);
    }

    private updateLocalModel(model: string, propertyName: string, newValue: any) {
        const objModel = { ...this.state[model] };
        objModel[propertyName] = newValue;

        this.setState({
            [model]: objModel,
        });
    }

    private validCropYield() {
        return (
            this.state.insurance.cropGuid ||
            this.state.insurance.cropPurposeGuid ||
            this.state.insurance.yieldUnitGuid ||
            this.state.insurance.averageYield ||
            this.state.insurance.countyTYield
        );
    }

    private addCropYield() {
        const newRecord = { ...this.state.insurance };
        newRecord.cropName = this.getLabelByValue(
            this.cropLocalOptions,
            this.state.insurance.cropGuid
        );
        newRecord.cropPurposeName = this.getLabelByValue(
            this.cropPurposeLocalOptions,
            this.state.insurance.cropPurposeGuid
        );
        newRecord.yieldUnitName = this.getLabelByValue(
            this.yieldUnitListOptions,
            this.state.insurance.yieldUnitGuid
        );

        const newCropYieldList = [...this.props.activeField.fieldCropYieldList, newRecord];

        this.cropLocalOptions = this.clearSelection(this.cropLocalOptions);
        this.cropPurposeLocalOptions = this.clearSelection(this.cropPurposeLocalOptions);
        this.yieldUnitListOptions = this.clearSelection(this.yieldUnitListOptions);
        this.updateFieldModel("fieldCropYieldList", newCropYieldList);

        this.setState({
            insurance: {
                cropGuid: "",
                cropName: "",
                cropPurposeGuid: "",
                cropPurposeName: "",
                yieldUnitGuid: "",
                yieldUnitName: "",
                countyTYield: null,
                averageYield: null,
                calculatedYield: null,
                fieldGuid: "",
                fieldCropYieldGuid: "",
            },
            agvanceCustomerList: [],
        });
    }

    private removeCropYield(key: any) {
        const fieldCropYieldList = [
            ...this.props.activeField.fieldCropYieldList?.slice(0, key),
            ...this.props.activeField.fieldCropYieldList?.slice(key + 1),
        ];
        this.updateFieldModel("fieldCropYieldList", fieldCropYieldList);
    }

    private isNewField() {
        return (
            this.props.activeField?.fieldGuid === null || this.props.activeField?.fieldGuid === ""
        );
    }

    private getNewBillSplit(
        customerId: string,
        customerFirstName: string,
        customerLastName: string
    ) {
        return {
            splitCustomerID: customerId,
            splitCustomerFirstName: customerFirstName,
            splitCustomerLastName: customerLastName,
            split1Percent: 100,
            split2Percent: 100,
            split3Percent: 100,
        } as FieldAPI.IBillSplit;
    }

    private updateFarmName(v: string): void {
        const { agvanceFarmSplits } = this.props;
        if (
            this.isAgvanceConnected() &&
            agvanceFarmSplits.length > 0 &&
            !this.disableBillSplitsEditing()
        ) {
            const selectedItem =
                this.state.agvanceCustomer && this.props.agvanceOrgList.length > 1
                    ? this.props.agvanceOrgList.filter((record) => {
                          return record.agvanceCustomerGuid === this.state.agvanceCustomer;
                      })
                    : this.props.agvanceOrgList;
            const matchedFarm = agvanceFarmSplits.find(
                (farmSplit) =>
                    farmSplit.farmName === v &&
                    selectedItem.length > 0 &&
                    selectedItem[0].agvanceGrowerId === farmSplit.growerId
            );
            const farmBillSplits = matchedFarm ? matchedFarm.farmSplits : [];
            const sameFarmSplits =
                farmBillSplits.length === 1 &&
                farmBillSplits.length === this.state.billSplits.length &&
                farmBillSplits[0].splitCustomerID === this.state.billSplits[0].splitCustomerID;
            if (farmBillSplits.length > 0 && !this.isNewField() && !sameFarmSplits) {
                this.setState({
                    showFarmSplitConfirmDialog: true,
                    farmForBillingSplits: matchedFarm,
                });
            } else if (
                farmBillSplits.length > 0 &&
                this.isNewField() &&
                this.state.billSplitLevel !== BillSplitLevel.Field
            ) {
                if (!(v === "" || v === null)) {
                    // Adding New Farm not in Agvance
                    this.updateSplitInFieldModel(
                        farmBillSplits,
                        matchedFarm?.split2Name,
                        matchedFarm?.split3Name
                    );
                    this.setState({
                        billSplits: farmBillSplits,
                        billSplitLevel: BillSplitLevel.Farm,
                    });
                } else {
                    // Farm name is cleared
                    const names = this.props.agvanceOrgList[0].agvanceGrowerName.split(" ");
                    const defaultBillSplit = this.getNewBillSplit(
                        this.props.agvanceOrgList[0].agvanceGrowerId,
                        names[0],
                        names.length > 1 ? names[1] : ""
                    );
                    this.updateSplitInFieldModel([defaultBillSplit], null, null);
                    this.setBillSplitsDetails(
                        [defaultBillSplit],
                        null,
                        null,
                        BillSplitLevel.Default
                    );
                }
            }
        }
        this.updateFieldModel("farmName", v);
    }

    private updateSplitInFieldModel(splits: FieldAPI.IBillSplit[], split2Name, split3Name): void {
        const { updateFieldModel, activeField } = this.props;
        updateFieldModel({
            ...activeField,
            s2SplitArrangement: split2Name,
            s3SplitArrangement: split3Name,
            billingSplits: splits,
        });
    }

    private addFarmSplitsToField(): void {
        const farmBillingSplits = this.state.farmForBillingSplits.farmSplits;
        this.updateSplitInFieldModel(
            farmBillingSplits,
            this.state.farmForBillingSplits.split2Name,
            this.state.farmForBillingSplits.split3Name
        );
        this.setState({
            billSplits: farmBillingSplits,
            billSplitLevel: BillSplitLevel.Farm,
            farmForBillingSplits: null,
        });
        this.closeFarmSplitConfirmModal();
    }

    private validSplitBilling() {
        return (
            (this.props.agvanceFieldData || this.isNewField()) &&
            this.state.billSplits &&
            this.state.billSplits?.length < 8
        );
    }

    private addBillSplit() {
        const newRecord = {
            splitCustomerID: "",
            splitCustomerFirstName: "",
            splitCustomerLastName: "",
            split1Percent: 0,
            split2Percent: 0,
            split3Percent: 0,
        } as FieldAPI.IBillSplit;
        const newBillSplitList = [...this.state.billSplits, newRecord];
        this.updateFieldModel("billingSplits", newBillSplitList);

        this.setState({
            billSplits: newBillSplitList,
        });
    }

    private removeBillSplit(key: number) {
        const billSplitList = [
            ...this.state.billSplits?.slice(0, key),
            ...this.state.billSplits?.slice(key + 1),
        ];

        this.updateFieldModel("billingSplits", billSplitList);
        this.setState({
            billSplits: billSplitList,
            billSplitIndexToDelete: null,
            showBillSplitsDeleteDialog: false,
        });
    }

    private clearCustomerFromSplit(index: number) {
        this.updateBillSplitCustomer(index, {
            growerId: null,
            firstName: null,
            lastName: null,
            agvanceGuid: null,
        });
    }

    private updateBillSplit(index: number, key: string, value: any) {
        const billSplitToUpdate = this.state.billSplits[index];
        billSplitToUpdate[key] = value;

        this.updateFieldModel("billingSplits", [...this.state.billSplits]);
        this.setState({
            billSplits: [...this.state.billSplits],
        });
    }

    private updateBillSplitCustomer(index: number, customer: any) {
        if (!customer) {
            return;
        }
        const billSplitToUpdate = this.state.billSplits[index];
        billSplitToUpdate.splitCustomerID = customer.growerId;
        billSplitToUpdate.splitCustomerFirstName = customer.firstName;
        billSplitToUpdate.splitCustomerLastName = customer.lastName;

        this.updateFieldModel("billingSplits", this.state.billSplits);
        this.setState({
            billSplits: [...this.state.billSplits],
        });
    }

    private joinNames(firstName: string, lastName: string) {
        const strings = [firstName, lastName];
        return strings.filter(Boolean).join(" ");
    }

    private onAgvanceCustomerChange(guid: string) {
        const selectedItem = this.props.agvanceOrgList.filter((record) => {
            return record.agvanceCustomerGuid === guid;
        });
        let modifiedFieldModel = {
            ...this.props.activeField,
        };
        if (selectedItem?.length) {
            let billSplitsList: FieldAPI.IBillSplit[] = [];
            const billSplits = {
                s2SplitArrangement: null,
                s3SplitArrangement: null,
                billingSplits: billSplitsList,
            };
            if (this.isNewField()) {
                const names = selectedItem[0].agvanceGrowerName.split(" ");
                const defaultBillSplit = this.getNewBillSplit(
                    selectedItem[0].agvanceGrowerId,
                    names[0],
                    names.length > 1 ? names[1] : ""
                );
                billSplitsList = this.state.billSplits;
                billSplitsList[0].splitCustomerID = defaultBillSplit.splitCustomerID;
                billSplitsList[0].splitCustomerFirstName = defaultBillSplit.splitCustomerFirstName;
                billSplitsList[0].splitCustomerLastName = defaultBillSplit.splitCustomerLastName;
                billSplits.billingSplits = billSplitsList;
                this.setState({
                    billSplits: billSplitsList,
                    billSplitLevel: BillSplitLevel.Default,
                });
                modifiedFieldModel = {
                    ...this.props.activeField,
                    fieldName: "",
                    farmName: "",
                };
            }
            this.setState({
                agvanceCustomer: guid,
            });
            this.updateFieldForAgvanceCustomers(selectedItem[0], modifiedFieldModel, billSplits);
        }
    }

    private setBillSplitsDetails(
        billSplits: FieldAPI.IBillSplit[],
        split2Name: string,
        split3Name: string,
        level: BillSplitLevel
    ) {
        const billSplitDetails = {} as any;
        billSplitDetails.s2SplitArrangement = split2Name;
        billSplitDetails.s3SplitArrangement = split3Name;
        billSplitDetails.billingSplits = billSplits;
        this.setState({
            billSplits: billSplitDetails.billingSplits,
            billSplitLevel: level,
        });
        return billSplitDetails;
    }

    private onFieldNameSelection(
        fieldName = "",
        agvanceFieldGuid = "",
        agvanceFieldId = "",
        farmName = "",
        locationId = "(NONE)",
        growerId = "",
        growerFirstName = "",
        growerLastName = ""
    ) {
        const { updateFieldModel, activeField, agvanceFarmSplits } = this.props;
        let billSplitDetails = {} as any;
        if (this.isAgvanceConnected() && !(agvanceFieldGuid === null || agvanceFieldGuid === "")) {
            // Agvance field selected from drop-down. Need split info from API
            const agvanceMatchedField = this.props.agvanceFieldList.find(
                (field) => field.agvanceGuid === agvanceFieldGuid
            );
            if (agvanceMatchedField && agvanceMatchedField.billingSplits.length > 0) {
                billSplitDetails = this.setBillSplitsDetails(
                    agvanceMatchedField.billingSplits,
                    agvanceMatchedField.split2Name,
                    agvanceMatchedField.split3Name,
                    BillSplitLevel.Field
                );
            }
        } else if (
            this.isAgvanceConnected() &&
            this.isNewField() &&
            (agvanceFieldGuid === null || agvanceFieldGuid === "") &&
            farmName !== ""
        ) {
            const selectedItem =
                this.state.agvanceCustomer && this.props.agvanceOrgList.length > 1
                    ? this.props.agvanceOrgList.filter((record) => {
                          return record.agvanceCustomerGuid === this.state.agvanceCustomer;
                      })
                    : this.props.agvanceOrgList;
            // Field entered but farm exists. Also handled in update Farm
            const matchedFarm = agvanceFarmSplits.find(
                (farmSplit) =>
                    farmSplit.farmName === farmName &&
                    selectedItem.length > 0 &&
                    selectedItem[0].agvanceGrowerId === farmSplit.growerId
            );
            const farmBillSplits = matchedFarm ? matchedFarm.farmSplits : [];

            if (farmBillSplits.length > 0) {
                billSplitDetails = this.setBillSplitsDetails(
                    farmBillSplits,
                    matchedFarm.split2Name,
                    matchedFarm.split3Name,
                    BillSplitLevel.Farm
                );
            } else {
                billSplitDetails = this.setBillSplitsDetails(
                    [this.getNewBillSplit(growerId, growerFirstName, growerLastName)],
                    null,
                    null,
                    BillSplitLevel.Default
                );
            }
        } else if (this.isNewField() && this.isAgvanceConnected() && fieldName === "") {
            // Field name is cleared
            const names = this.props.agvanceOrgList[0].agvanceGrowerName.split(" ");
            const defaultBillSplit = this.getNewBillSplit(
                this.props.agvanceOrgList[0].agvanceGrowerId,
                names[0],
                names.length > 1 ? names[1] : ""
            );
            billSplitDetails = this.setBillSplitsDetails(
                [defaultBillSplit],
                null,
                null,
                BillSplitLevel.Default
            );
        }
        const newFieldModel = {
            ...activeField,
            fieldName,
            agvanceFieldGuid: agvanceFieldGuid || activeField.agvanceFieldGuid,
            agvanceFieldId: agvanceFieldId || activeField.agvanceFieldId,
            agvanceLocationId: locationId,
            farmName,
            ...billSplitDetails,
        };
        updateFieldModel(newFieldModel);
    }

    private getAgvanceCustomer(agvanceOrgList: any[], activeField: any) {
        const selectedValue = activeField.fieldGuid
            ? agvanceOrgList.filter(
                  ({ agvanceCustomerGuid }) =>
                      agvanceCustomerGuid === activeField.agvanceCustomerGuid
              )
            : agvanceOrgList.filter(({ isPrimary }) => isPrimary);
        if (selectedValue?.length) {
            // Update bill splits
            let billSplitDetails = {} as any;
            if (this.state.billSplits.length === 0) {
                const names = selectedValue[0].agvanceGrowerName.split(" ");
                const defaultBillSplit = this.getNewBillSplit(
                    selectedValue[0].agvanceGrowerId,
                    names[0],
                    names.length > 1 ? names[1] : ""
                );
                billSplitDetails = this.setBillSplitsDetails(
                    [defaultBillSplit],
                    null,
                    null,
                    BillSplitLevel.Default
                );
            }

            this.updateFieldForAgvanceCustomers(selectedValue[0], activeField, {
                ...billSplitDetails,
                ...this.keepSavedAgvanceLocation(activeField),
            });
            return selectedValue[0].agvanceCustomerGuid;
        }
        return null;
    }

    /* duplicated in combine-fields-modal.jsx */
    private showAdditionalAgvanceCustomer(nextProps?: IProps) {
        const { agvanceOrgList } = nextProps || this.props;
        // Check for Agvance personality and availability of more than one Agvance customers
        return this.isAgvanceConnected() && agvanceOrgList?.length > 1;
    }

    private showAgvanceFieldClassifications(props: IProps = this.props) {
        const { agvanceFieldClassificationList } = props;
        return this.isAgvanceConnected() && agvanceFieldClassificationList?.length > 0;
    }

    private isAgvanceConnected() {
        return AgvanceUtils.isAgvanceConnected(this.props.personalityId);
    }

    private getAgvanceFieldList() {
        if (!this.state.agvanceCustomer) {
            return this.props.agvanceFieldList;
        }
        return this.props.agvanceFieldList.filter(({ agvanceGrowerGuid }) => {
            return this.state.agvanceCustomer === agvanceGrowerGuid;
        });
    }

    private isRestrictedAgvanceLocation(locationId: string): boolean {
        const { agvanceLocationsList } = this.props;
        return (
            locationId != null &&
            locationId !== "" &&
            locationId !== "(NONE)" &&
            agvanceLocationsList?.length > 0 &&
            !agvanceLocationsList.some((location) => location.agvanceLocationId === locationId)
        );
    }

    private getSelectedAgvanceLocationId(): string {
        const locationId = this.props.activeField?.agvanceLocationId;

        if (
            locationId == null ||
            locationId === "" ||
            this.isRestrictedAgvanceLocation(locationId)
        ) {
            return "(NONE)";
        }
        return locationId;
    }

    private resetRestrictedAgvanceLocation(): void {
        const { activeField, userInfo } = this.props;

        if (!this.isAgvanceConnected() || !userInfo?.role?.agvanceLocation) {
            return;
        }
        if (this.isRestrictedAgvanceLocation(activeField?.agvanceLocationId)) {
            this.updateFieldModel("agvanceLocationId", "(NONE)");
        }
    }

    private closeCertOrganicVerifyModal() {
        this.setState({ showCertOrganicChangeDialog: false });
    }

    private closeFarmSplitConfirmModal() {
        this.setState({ showFarmSplitConfirmDialog: false });
    }

    #filterAgvanceFieldClassifications(classifications: number[]): FieldAPI.IAgvClassification[] {
        return this.props.agvanceFieldClassificationList.filter((c) =>
            classifications?.some((k) => c.classificationKey === k)
        );
    }

    private onBillableAcreChangeDebounce = AppHelpers.debounce(() => {
        logFirebaseEvent("field_setup_agvance_billable_area");
    }, 1000);

    private _isDisabled() {
        return (
            this.isAgvanceConnected() &&
            this.props.agvanceOrgList?.length > 1 &&
            this.props.agvanceCustomerList?.length === 0 &&
            !this.props.userInfo?.role?.agvanceBillSplitsEditing
        );
    }

    private disableBillSplitsEditing() {
        return !this.props.userInfo?.role?.agvanceBillSplitsEditing;
    }

    /// ------------- ///
    render() {
        const { agvanceFieldAcres } = this.state;
        const { formatMessage } = this.props.intl;
        const { agvanceOrgList, userInfo } = this.props;
        const isFieldNameError = false;
        const isFieldBoundaryError = false;
        let errorCodeList = [];
        const { agvanceCustomer } = this.state;
        if (this.props.apiError && this.props.apiError.errorCodeList) {
            errorCodeList = this.props.apiError.errorCodeList;
        }
        const isMainCropUnlinked = isCropUnlinked(
            this.props.activeField.cropGuid,
            this.props.cropData
        );
        const isInsuranceCropUnlinked = isCropUnlinked(
            this.state.insurance.cropGuid,
            this.props.cropData
        );
        const cropNotLinkedText = formatMessage(globalMessages.cropNotLinkedInAgvance);

        return (
            <div
                className={classNames(
                    "information-detail-container",
                    "field-information-detail-container"
                )}
            >
                <div className={classNames("information-detail", "field-information-detail")}>
                    {isFieldNameError && (
                        <div className="field-name-error-msg">
                            {formatMessage(messages.fieldNameErrorMsg)}
                        </div>
                    )}
                    {isFieldBoundaryError && (
                        <div className="field-boundary-error-msg">
                            {formatMessage(messages.fieldBoundaryErrorMsg)}
                        </div>
                    )}
                    {/* General Information */}
                    <Bucket
                        className="detail-bucket general-info-bucket"
                        showSymbol={false}
                        isCollapsible={false}
                        isExpanded={true}
                        testId="field-info-general-info-bucket"
                    >
                        <BucketHeader className="detail-bucket-header">
                            {formatMessage(messages.generalInfoText)}
                        </BucketHeader>
                        {/* Field Name */}
                        <SearchAgvanceField
                            autoFocus
                            openOnFocus={false}
                            errorCodes={errorCodeList}
                            value={this.props.activeField.fieldName}
                            onInputChange={(v) => this.updateFieldModel("fieldName", v)}
                            onChange={this.onFieldNameSelection.bind(this)}
                            records={this.getAgvanceFieldList()}
                        />
                        {/*Lock Boundary*/}
                        {userInfo.role.lockBoundary ? (
                            <Checkbox
                                value={this.props.activeField.lockBoundary}
                                label={formatMessage(messages.lockBoundary)}
                                onChange={(e, v) => {
                                    this.updateFieldModel("lockBoundary", v);
                                }}
                                testId="field-info-lock-boundary-checkbox"
                            />
                        ) : null}
                        {!this.showAdditionalAgvanceCustomer() ? null : (
                            <SelectInput
                                optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                clearFilterInputOnBlur={false}
                                placeholderText={formatMessage(globalMessages.agvanceCustomer)}
                                value={agvanceCustomer}
                                containerClassNames={[
                                    "field-agvance-customer",
                                    {
                                        "form-input-error": errorCodeList.some((item) =>
                                            [
                                                869,
                                                870,
                                                871,
                                                "Agvance.LocationIsRequired",
                                                "Agvance.CustomerIsRequired",
                                                "Agvance.FieldIsRequired",
                                            ].includes(item)
                                        ),
                                    },
                                ]}
                                onChange={(v) => this.onAgvanceCustomerChange(v)}
                                options={agvanceOrgList.map(
                                    ({
                                        agvanceGrowerName,
                                        customerName,
                                        agvanceCustomerGuid,
                                        orgLevelName,
                                        isPrimary,
                                        alternateCustomerIdSelecting,
                                    }) => ({
                                        label: `${
                                            isPrimary ? customerName : agvanceGrowerName
                                        } > ${orgLevelName}`,
                                        titleValue: `${agvanceGrowerName} - ${alternateCustomerIdSelecting}`,
                                        value: agvanceCustomerGuid,
                                    })
                                )}
                                optionRenderer={optionRendererWithCustomTitle}
                                required
                                testId="field-info-agvance-customer-select"
                            />
                        )}
                        {/* Farm Name */}
                        <SelectInput
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            clearFilterInputOnBlur={false}
                            options={this.farmListOptions}
                            noOptionsRenderer={null}
                            onInputChange={(v) => this.updateFarmName(v)}
                            onChange={(v) => this.updateFarmName(v)}
                            placeholderText={formatMessage(messages.fieldFarmNamePlaceholderText)}
                            value={this.props.activeField.farmName}
                            initialFilterStr={this.props.activeField.farmName}
                            maxLength={50}
                            allowEmptyOptions
                            testId="field-info-farm-name-select"
                        />
                        {/*Certified Organic*/}
                        <Checkbox
                            value={this.props.activeField.certifiedOrganic}
                            label={formatMessage(messages.certifiedOrganicText)}
                            onChange={(e, v) => {
                                this.setState({
                                    newCertOrganicValue: v,
                                    showCertOrganicChangeDialog: true,
                                });
                            }}
                            testId="field-info-certified-organic-checkbox"
                        />
                        {/* Irrigated */}
                        <SelectInput
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            containerClassNames={["irrigation-select"]}
                            options={this.irrigatedListOptions}
                            onChange={(v) => this.updateFieldModel("irrigated", v)}
                            value={this.props.activeField.irrigated}
                            placeholderText={formatMessage(messages.irrigatedPlaceHolderText)}
                            testId="field-info-irrigated-select"
                        />
                        {/*Drainage Tile*/}
                        <Checkbox
                            value={this.props.activeField.drainageTile}
                            label={formatMessage(messages.drainageTileText)}
                            onChange={(e, v) => {
                                this.updateFieldModel("drainageTile", v);
                            }}
                            testId="field-info-drainage-tile-checkbox"
                        />
                        {/*Irrigation Type*/}
                        {!this.props.activeField.irrigated ? null : (
                            <SelectInput
                                optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                containerClassNames={["irrigation-select"]}
                                options={this.irrigationTypeListOptions}
                                onChange={(v) => this.updateFieldModel("irrigationTypeGuid", v)}
                                value={this.props.activeField.irrigationTypeGuid}
                                placeholderText={formatMessage(
                                    messages.irrigationTypePlaceholderText
                                )}
                                testId="field-info-irrigation-type-select"
                            />
                        )}
                        {/*Agvance Field Classifications*/}
                        {!this.showAgvanceFieldClassifications() ? null : (
                            <SelectInputMulti
                                key={`classification${this.state.multiSelectIndex}`}
                                isClearable={true}
                                options={this.props.agvanceFieldClassificationList
                                    .map((c) => ({
                                        value: c.classificationKey,
                                        label: c.classificationName,
                                    }))
                                    .sort((a, b) => (b.label > a.label ? -1 : 1))}
                                placeholder={formatMessage(messages.fieldClassificationText)}
                                onChange={(classifications: number[]) => {
                                    logFirebaseEvent("field_field_classifications");
                                    this.updateFieldModel(
                                        "classifications",
                                        this.#filterAgvanceFieldClassifications(classifications)
                                    );
                                }}
                                showTopLabel={true}
                                sort={true}
                                value={this.props.activeField.classifications
                                    ?.map((c: FieldAPI.IAgvClassification) => ({
                                        value: c.classificationKey,
                                        label: c.classificationName,
                                    }))
                                    .sort((a, b) => (b.label > a.label ? -1 : 1))}
                            />
                        )}
                        {/*Agvance Billable Area*/}
                        {!(
                            this.isAgvanceConnected() && userInfo?.role?.agvanceBillableArea
                        ) ? null : (
                            <NumericInput
                                onChange={(value) => {
                                    if (value != this.state.agvanceFieldAcres) {
                                        this.onBillableAcreChangeDebounce();
                                    }
                                    this.setState({ agvanceFieldAcres: value });
                                    this.updateFieldModel("agvanceBillableArea", value);
                                }}
                                placeholderText={"Agvance Billable Area"}
                                value={
                                    agvanceFieldAcres != null && agvanceFieldAcres > 0
                                        ? agvanceFieldAcres
                                        : null
                                }
                                testId="field-info-agvance-billable-area-input"
                            />
                        )}
                        {/*Agvance Locations*/}
                        {!(this.isAgvanceConnected() && userInfo?.role?.agvanceLocation) ? null : (
                            <SelectInput
                                clearable={false}
                                containerClassNames={["agvance-location-select"]}
                                options={this.props.agvanceLocationsList
                                    ?.map((c) => {
                                        const label =
                                            c.name.toLowerCase() === "none"
                                                ? c.name
                                                : `${c.agvanceLocationId} - ${c.name}`;
                                        return {
                                            value: c.agvanceLocationId,
                                            label,
                                        };
                                    })
                                    .sort((a, b) => {
                                        if (a.label.toLowerCase() === "none") {
                                            return -1;
                                        }
                                        if (b.label.toLowerCase() === "none") {
                                            return 1;
                                        }
                                        return b.label > a.label ? -1 : 1;
                                    })}
                                onChange={(v) => this.updateFieldModel("agvanceLocationId", v)}
                                value={this.getSelectedAgvanceLocationId()}
                                placeholderText={formatMessage(
                                    messages.agvanceLocationIdPlaceholderText
                                )}
                                required={true}
                                testId="field-info-agvance-location-select"
                            />
                        )}
                        {/*Directions*/}
                        <TextArea
                            maxLength={4000}
                            value={
                                this.props.activeField.directions
                                    ? this.props.activeField.directions
                                    : ""
                            }
                            onChange={(v) => {
                                this.updateFieldModel("directions", v);
                            }}
                            placeholderText={formatMessage(messages.directionsPlaceholderText)}
                            testId="field-info-directions-textarea"
                        />
                        {/*Field Notes*/}
                        <TextArea
                            maxLength={4000}
                            value={
                                this.props.activeField.fieldNotes
                                    ? this.props.activeField.fieldNotes
                                    : ""
                            }
                            onChange={(v) => {
                                this.updateFieldModel("fieldNotes", v);
                            }}
                            placeholderText={formatMessage(messages.fieldNotesPlaceholderText)}
                            testId="field-info-field-notes-textarea"
                        />
                    </Bucket>

                    {/* Crop Details */}
                    <Bucket
                        className="detail-bucket crop-detail-bucket"
                        testId="field-info-crop-detail-bucket"
                    >
                        <BucketHeader className="detail-bucket-header">
                            {formatMessage(messages.cropInfoText)}
                        </BucketHeader>
                        <SelectInput
                            options={this.cropListOptions}
                            optionIsHiddenKey={IS_SELECTABLE}
                            placeholderText={formatMessage(messages.cropPlaceholderText)}
                            onChange={(v) => {
                                this.updateFieldModel("cropGuid", v);
                            }}
                            value={this.props.activeField.cropGuid}
                            testId="field-info-crop-select"
                            hasError={isMainCropUnlinked}
                            toolTip={isMainCropUnlinked ? cropNotLinkedText : undefined}
                        />
                        <SelectInput
                            options={this.cropPurposeListOptions}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            placeholderText={formatMessage(messages.cropPurposePlaceholderText)}
                            onChange={(v) => {
                                this.updateFieldModel("cropPurposeGuid", v);
                            }}
                            value={this.props.activeField.cropPurposeGuid}
                            testId="field-info-crop-purpose-select"
                        />
                        <SelectInput
                            options={this.brandListOptions}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            placeholderText={formatMessage(messages.brandPlaceholderText)}
                            disabled={
                                !(
                                    this.brandListOptions?.length > 0 &&
                                    this.props.activeField.cropGuid
                                )
                            }
                            onChange={(v) => {
                                this.updateFieldModel("brandOrganizationGuid", v);
                            }}
                            value={this.props.activeField.brandOrganizationGuid}
                            testId="field-info-brand-select"
                        />
                        <SelectInput
                            options={this.varietyListOptions}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            placeholderText={formatMessage(messages.varietyPlaceholderText)}
                            disabled={
                                !(this.varietyListOptions?.length > 0) ||
                                !this.props.activeField.brandOrganizationGuid
                            }
                            onChange={(v) => {
                                this.updateFieldModel("varietyHybridGuid", v);
                            }}
                            value={this.props.activeField.varietyHybridGuid}
                            testId="field-info-variety-select"
                        />
                        <DateInput
                            openDirection={"left"}
                            value={
                                this.props.activeField.cropPlantingDate
                                    ? moment(this.props.activeField.cropPlantingDate)
                                    : null
                            }
                            onChange={(v) => {
                                this.updateFieldModel("cropPlantingDate", v);
                            }}
                            placeholderText={formatMessage(messages.plantingDatePlaceholderText)}
                            testId="field-info-planting-date-date"
                        />
                    </Bucket>
                    {/*USDA Information*/}
                    <Bucket
                        className="detail-bucket usda-detail-bucket"
                        testId="field-info-usda-detail-bucket"
                    >
                        <BucketHeader className="detail-bucket-header">
                            {formatMessage(messages.usdaInfoText)}
                        </BucketHeader>
                        {/*Legal Description*/}
                        <TextArea
                            maxLength={4000}
                            value={this.props.activeField.legalDescription || ""}
                            onChange={(v) => {
                                this.updateFieldModel("legalDescription", v);
                            }}
                            placeholderText={formatMessage(
                                messages.legalDescriptionPlaceholderText
                            )}
                            testId="field-info-legal-description-textarea"
                        />
                        {/*FSA (Acres)*/}
                        <NumericInput
                            scale={2}
                            precision={12}
                            value={this.props.activeField.fsaAcres}
                            onChange={(v) => {
                                this.updateFieldModel("fsaAcres", v);
                            }}
                            placeholderText={formatMessage(messages.fsaAcresPlaceholderText)}
                            testId="field-info-fsa-acres-input"
                        />
                        {/*CLU acres*/}
                        <NumericInput
                            scale={2}
                            precision={12}
                            value={this.props.activeField.cluAcres}
                            onChange={(v) => {
                                this.updateFieldModel("cluAcres", v);
                            }}
                            placeholderText={formatMessage(messages.cluAcresPlaceholderText)}
                            testId="field-info-clu-acres-input"
                        />

                        {this.props.editFieldGuid && (
                            <div className="bucket-content">
                                {/*State*/}
                                <TextInput
                                    maxLength={2}
                                    value={this.props.activeField.fieldBoundary?.stateAbbr}
                                    placeholderText={formatMessage(messages.statePlaceholderText)}
                                    disabled={true}
                                    testId="field-info-state-input"
                                />
                                {/*County*/}
                                <TextInput
                                    maxLength={50}
                                    value={this.props.activeField.fieldBoundary?.county}
                                    placeholderText={formatMessage(messages.countyPlaceholderText)}
                                    disabled={true}
                                    testId="field-info-county-input"
                                />
                                {/*Township*/}
                                <TextInput
                                    maxLength={9}
                                    value={this.props.activeField.fieldBoundary?.township}
                                    placeholderText={formatMessage(
                                        messages.townshipPlaceholderText
                                    )}
                                    disabled={true}
                                    testId="field-info-township-input"
                                />
                                {/*Range*/}
                                <TextInput
                                    maxLength={9}
                                    value={this.props.activeField.fieldBoundary?.range}
                                    placeholderText={formatMessage(messages.rangePlaceholderText)}
                                    disabled={true}
                                    testId="field-info-range-input"
                                />
                                {/*Section*/}
                                <TextInput
                                    maxLength={3}
                                    value={this.props.activeField.fieldBoundary?.section}
                                    placeholderText={formatMessage(messages.sectionPlaceholderText)}
                                    disabled={true}
                                    testId="field-info-section-input"
                                />
                                {/*APN*/}
                                <TextInput
                                    maxLength={20}
                                    value={this.props.activeField.apn}
                                    onChange={(v) => {
                                        this.updateFieldModel("apn", v);
                                    }}
                                    placeholderText={formatMessage(messages.apnPlaceholderText)}
                                    testId="field-info-apn-input"
                                />
                            </div>
                        )}

                        {/*CLU ID*/}
                        <NumericInput
                            scale={0}
                            precision={9}
                            value={this.props.activeField.cluId}
                            suppressFormatting={true}
                            onChange={(v) => {
                                this.updateFieldModel("cluId", v);
                            }}
                            placeholderText={formatMessage(messages.cluIdPlaceholderText)}
                            testId="field-info-clu-id-input"
                        />
                        {/*FSA Farm Number*/}
                        <NumericInput
                            scale={0}
                            precision={9}
                            value={this.props.activeField.fsaFarmNum}
                            onChange={(v) => {
                                this.updateFieldModel("fsaFarmNum", v);
                            }}
                            placeholderText={formatMessage(messages.fsaFarmNumberPlaceholderText)}
                            suppressFormatting={true}
                            testId="field-info-fsa-farm-num-input"
                        />
                        {/*FSA Field Number*/}
                        <NumericInput
                            scale={0}
                            precision={9}
                            value={this.props.activeField.fsaFieldNum}
                            onChange={(v) => {
                                this.updateFieldModel("fsaFieldNum", v);
                            }}
                            placeholderText={formatMessage(messages.fsaFieldNumberPlaceholderText)}
                            suppressFormatting={true}
                            testId="field-info-fsa-field-num-input"
                        />
                        {/*FSA Tract Number*/}
                        <NumericInput
                            scale={0}
                            precision={9}
                            value={this.props.activeField.fsaTractNum}
                            onChange={(v) => {
                                this.updateFieldModel("fsaTractNum", v);
                            }}
                            placeholderText={formatMessage(messages.fsaTractNumberPlaceholderText)}
                            suppressFormatting={true}
                            testId="field-info-fsa-tract-num-input"
                        />

                        {/*Watershed Name*/}
                        <TextInput
                            maxLength={50}
                            value={this.props.activeField.fieldBoundary?.watershedName}
                            onChange={(v) => {
                                this.updateFieldModel("fieldBoundary.watershedName", v);
                            }}
                            placeholderText={formatMessage(messages.watershedNamePlaceholderText)}
                            testId="field-info-watershed-name-input"
                        />
                        {/*Watershed Number*/}
                        <TextInput
                            maxLength={12}
                            value={this.props.activeField.fieldBoundary?.watershedNum}
                            onChange={(v) => {
                                this.updateFieldModel("fieldBoundary.watershedNum", v);
                            }}
                            placeholderText={formatMessage(messages.watershedNumberPlaceholderText)}
                            testId="field-info-watershed-num-input"
                        />

                        <div className="bucket-content">
                            {/*Dominant Soil Series*/}
                            <TextInput
                                value={this.props.activeField.fieldBoundary?.soilSeries}
                                placeholderText={formatMessage(
                                    messages.dominantSoilSeriesPlaceholderText
                                )}
                                disabled={true}
                                testId="field-info-soil-series-input"
                            />
                            {/*Dominant Soil Texture*/}
                            <TextInput
                                value={this.props.activeField.fieldBoundary?.soilTexture}
                                placeholderText={formatMessage(
                                    messages.dominantSoilTexturePlaceholderText
                                )}
                                disabled={true}
                                testId="field-info-soil-texture-input"
                            />
                        </div>
                    </Bucket>
                    {/* Insurance Information */}
                    <Bucket
                        className="detail-bucket insurance-detail-bucket"
                        testId="field-info-insurance-detail-bucket"
                    >
                        <BucketHeader
                            className={classNames("detail-bucket-header", {
                                "label-error": errorCodeList.some((item) =>
                                    [
                                        697,
                                        742,
                                        "Field.FieldYieldUnitRequired",
                                        "Field.FieldYieldUnitDependentRequired",
                                    ].includes(item)
                                ),
                            })}
                        >
                            {formatMessage(messages.insuranceText)}
                        </BucketHeader>
                        {/*Insurance Policy Number*/}
                        <TextInput
                            maxLength={20}
                            value={this.props.activeField.insuranceNum}
                            onChange={(v) => {
                                this.updateFieldModel("insuranceNum", v);
                            }}
                            placeholderText={formatMessage(
                                messages.insurancePolicyNumPlaceholderText
                            )}
                            testId="field-info-insurance-num-input"
                        />
                        {/*Insurance Policy type*/}
                        <SelectInput
                            options={this.insurancePolicyTypeListOptions}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            placeholderText={formatMessage(
                                messages.insurancePolicyTypePlaceholderText
                            )}
                            onChange={(v) => {
                                this.updateFieldModel("insurancePolicyTypeGuid", v);
                            }}
                            value={this.props.activeField.insurancePolicyTypeGuid}
                            testId="field-info-insurance-policy-type-select"
                        />
                        {/*High-Risk Land (TF)*/}
                        <div className="high-risk-land">
                            <Checkbox
                                value={this.props.activeField.highRiskLandYn}
                                label={formatMessage(messages.highRiskLandText)}
                                onChange={(e, v) => {
                                    this.updateFieldModel("highRiskLandYn", v);
                                }}
                                testId="field-info-high-risk-land-checkbox"
                            />
                        </div>
                        {/*Insurance Information Per Crop*/}
                        <div className="detail-header">
                            {formatMessage(messages.insuraceInfoByCropLabel)}
                        </div>
                        <div className="insurance-per-crop">
                            {/* Crop */}
                            <SelectInput
                                options={this.cropLocalOptions}
                                optionIsHiddenKey={IS_SELECTABLE}
                                placeholderText={formatMessage(messages.cropPlaceholderText)}
                                onChange={(v) => {
                                    this.updateLocalModel("insurance", "cropGuid", v);
                                }}
                                value={this.state.insurance.cropGuid}
                                testId="field-info-insurance-crop-select"
                                hasError={isInsuranceCropUnlinked}
                                toolTip={isInsuranceCropUnlinked ? cropNotLinkedText : undefined}
                            />
                            {/* Crop Purpose */}
                            <SelectInput
                                options={this.cropPurposeLocalOptions}
                                optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                placeholderText={formatMessage(messages.cropPurposePlaceholderText)}
                                onChange={(v) => {
                                    this.updateLocalModel("insurance", "cropPurposeGuid", v);
                                }}
                                value={this.state.insurance.cropPurposeGuid}
                                testId="field-info-insurance-crop-purpose-select"
                            />

                            {/*Crop Yield Unit*/}
                            <SelectInput
                                options={this.yieldUnitListOptions}
                                optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                                placeholderText={formatMessage(messages.yieldUnitPlaceholderText)}
                                onChange={(v) => {
                                    this.updateLocalModel("insurance", "yieldUnitGuid", v);
                                }}
                                value={this.state.insurance.yieldUnitGuid}
                                testId="field-info-insurance-yield-unit-select"
                            />

                            {/*County T-Yield*/}
                            <NumericInput
                                scale={2}
                                precision={12}
                                value={this.state.insurance.countyTYield}
                                onChange={(v) => {
                                    this.updateLocalModel("insurance", "countyTYield", v);
                                }}
                                placeholderText={formatMessage(
                                    messages.countyTYieldPlaceholderText
                                )}
                                testId="field-info-county-t-yield-input"
                            />

                            {/* Average Yield */}
                            <NumericInput
                                scale={2}
                                precision={12}
                                value={this.state.insurance.averageYield}
                                onChange={(v) => {
                                    this.updateLocalModel("insurance", "averageYield", v);
                                }}
                                placeholderText={formatMessage(
                                    messages.averageYieldPlaceholderText
                                )}
                                testId="field-info-average-yield-input"
                            />

                            <NoLink
                                className={classNames(
                                    "listAddNoLink",
                                    !this.validCropYield() ? "disabled" : null
                                )}
                                label={formatMessage(messages.addCropYieldText)}
                                onClick={() => {
                                    if (this.validCropYield()) {
                                        this.addCropYield();
                                    }
                                }}
                            />

                            {this.props.activeField.fieldCropYieldList?.length > 0 &&
                                this.props.activeField.fieldCropYieldList?.map(
                                    (cropYield, index) => {
                                        return (
                                            <div
                                                key={index}
                                                className="detail-n-list field-crop-yield-list"
                                            >
                                                <div
                                                    className="trashcan-icon-container"
                                                    onClick={() => {
                                                        this.removeCropYield(index);
                                                    }}
                                                >
                                                    <TrashcanIcon className="trashcan-icon" />
                                                </div>
                                                <div className="data-container">
                                                    <div className="first-row">
                                                        {cropYield.cropName}
                                                        {cropYield.cropName &&
                                                            (cropYield.cropPurposeName ||
                                                                cropYield.yieldUnitName) &&
                                                            " - "}
                                                        {cropYield.cropPurposeName}
                                                        {cropYield.cropPurposeName &&
                                                            cropYield.yieldUnitName &&
                                                            " - "}
                                                        {cropYield.yieldUnitName}
                                                    </div>
                                                    <div className="second-row">
                                                        {cropYield.countyTYield}
                                                        {cropYield.countyTYield &&
                                                            (cropYield.averageYield ||
                                                                cropYield.calculatedYield) &&
                                                            " - "}
                                                        {cropYield.averageYield}
                                                        {cropYield.averageYield &&
                                                            cropYield.calculatedYield &&
                                                            " - "}
                                                        {cropYield.calculatedYield}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }
                                )}
                        </div>
                    </Bucket>

                    {/*LEASED INFO*/}
                    <Bucket
                        className="detail-bucket lease-detail-bucket"
                        testId="field-info-lease-detail-bucket"
                    >
                        <BucketHeader className="detail-bucket-header">
                            {formatMessage(messages.leaseInfoText)}
                        </BucketHeader>
                        {/*Lease Type*/}
                        <SelectInput
                            options={this.leaseTypeListOptions}
                            optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                            placeholderText={formatMessage(messages.leaseTypePlaceholderText)}
                            onChange={(v) => {
                                this.updateFieldModel("landOwnershipTypeGuid", v);
                            }}
                            value={this.props.activeField.landOwnershipTypeGuid}
                            testId="field-info-lease-type-select"
                        />

                        {/*Lease Expire Date (date)*/}
                        <DateInput
                            openDirection="right"
                            value={
                                this.props.activeField.leaseExpireDate
                                    ? moment(this.props.activeField.leaseExpireDate)
                                    : null
                            }
                            onChange={(v) => {
                                this.updateFieldModel("leaseExpireDate", v);
                            }}
                            placeholderText={formatMessage(messages.leaseExpireDatePlaceholderText)}
                            testId="field-info-lease-expire-date-date"
                        />
                    </Bucket>

                    {/*BILLING SPLITS INFO*/}
                    {this.isAgvanceConnected() && (
                        <Bucket
                            className="detail-bucket bill-splits-bucket"
                            testId="field-info-bill-splits-bucket"
                        >
                            <BucketHeader className="detail-bucket-header">
                                {formatMessage(messages.billSplitsInfoLabel)}
                            </BucketHeader>
                            <div className="bill-splits">
                                {this.state.billSplits?.length > 0 &&
                                    this.state.billSplits.map((split, index) => {
                                        const agvanceCustomerName = this.joinNames(
                                            split.splitCustomerFirstName,
                                            split.splitCustomerLastName
                                        );
                                        return (
                                            <React.Fragment key={index}>
                                                <div key={index} className="detail-n-list">
                                                    {/* Landlord Name */}
                                                    {index !== 0 &&
                                                    !this.disableBillSplitsEditing() ? (
                                                        <SearchAgvanceCustomer
                                                            reactKey={`search-customer-${index}`}
                                                            allowEmptyOptions
                                                            customerList={
                                                                this.props.agvanceCustomerList
                                                            }
                                                            disabled={
                                                                this._isDisabled() || index === 0
                                                            }
                                                            personalityId={this.props.personalityId}
                                                            initialValue={
                                                                agvanceCustomerName
                                                                    ? agvanceCustomerName
                                                                    : null
                                                            }
                                                            onSelection={(val) => {
                                                                const selectedCustomer =
                                                                    this.props.agvanceCustomerList.find(
                                                                        (customer) =>
                                                                            customer.growerName ===
                                                                            val
                                                                    );
                                                                if (selectedCustomer) {
                                                                    this.updateBillSplitCustomer(
                                                                        index,
                                                                        selectedCustomer
                                                                    );
                                                                    this.props.resetAgvanceCustomerList();
                                                                } else {
                                                                    this.clearCustomerFromSplit(
                                                                        index
                                                                    );
                                                                }
                                                            }}
                                                            placeholderText={formatMessage(
                                                                messages.agvanceCustomer
                                                            )}
                                                            className="customer-name-section"
                                                            isAgvanceCustomerListOutOfBound={true}
                                                            usePersonName={true}
                                                            customerSearchIsLoading={
                                                                !this.props
                                                                    .isAdditionalAgvanceCustomerSearch
                                                                    ? this.props
                                                                          .customerSearchIsLoading
                                                                    : false
                                                            }
                                                            valueTitle={`${agvanceCustomerName} - ${split.splitCustomerID}`}
                                                            showSearchIcon={false}
                                                            onInputChange={(value) => {
                                                                if (!value) {
                                                                    this.clearCustomerFromSplit(
                                                                        index
                                                                    );
                                                                    return;
                                                                }
                                                                if (
                                                                    this.props
                                                                        .isAgvanceCustomerListOutOfBound
                                                                ) {
                                                                    const orgLevelGuidList =
                                                                        this.props.agvanceOrgList.map(
                                                                            (item) =>
                                                                                item.orgLevelGuid
                                                                        );
                                                                    this.props.onFetchAgvanceCustomerWithNameSearch(
                                                                        {
                                                                            nameSearch: value,
                                                                            orgLevelGuidList,
                                                                        }
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <TextInput
                                                            key={`search-customer-${index}`}
                                                            required
                                                            disabled
                                                            maxLength={40}
                                                            value={agvanceCustomerName}
                                                            placeholderText={formatMessage(
                                                                messages.agvanceCustomer
                                                            )}
                                                            className="customer-name-text"
                                                            title={`${agvanceCustomerName} - ${split.splitCustomerID}`}
                                                            testId={`field-info-bill-split-customer-input-${index}`}
                                                        />
                                                    )}
                                                    {/* Split Percent 1 (%) */}
                                                    <NumericInput
                                                        disabled={
                                                            !split.splitCustomerID ||
                                                            this.disableBillSplitsEditing()
                                                        }
                                                        scale={4}
                                                        precision={7}
                                                        className="split-value"
                                                        value={split.split1Percent}
                                                        onChange={(v) => {
                                                            this.updateBillSplit(
                                                                index,
                                                                "split1Percent",
                                                                Number(v)
                                                            );
                                                        }}
                                                        placeholderText={formatMessage(messages.s1)}
                                                        labelTitle={formatMessage(
                                                            messages.s1SplitArrangement
                                                        )}
                                                        useRawTitle={false}
                                                        useValueTitle={true}
                                                        testId={`field-info-bill-split1-percent-input-${index}`}
                                                    />
                                                    {/* Split Percent 2 (%) */}
                                                    <NumericInput
                                                        disabled={
                                                            !split.splitCustomerID ||
                                                            this.disableBillSplitsEditing()
                                                        }
                                                        scale={4}
                                                        precision={7}
                                                        className="split-value"
                                                        value={split.split2Percent}
                                                        onChange={(v) => {
                                                            this.updateBillSplit(
                                                                index,
                                                                "split2Percent",
                                                                Number(v)
                                                            );
                                                        }}
                                                        placeholderText={formatMessage(messages.s2)}
                                                        labelTitle={
                                                            this.props.agvanceFieldData
                                                                ?.s2SplitArrangement
                                                                ? this.props.agvanceFieldData
                                                                      ?.s2SplitArrangement
                                                                : formatMessage(messages.s2)
                                                        }
                                                        useRawTitle={false}
                                                        useValueTitle={true}
                                                        testId={`field-info-bill-split2-percent-input-${index}`}
                                                    />
                                                    {/* Split Percent 3 (%)*/}
                                                    <NumericInput
                                                        disabled={
                                                            !split.splitCustomerID ||
                                                            this.disableBillSplitsEditing()
                                                        }
                                                        scale={4}
                                                        precision={7}
                                                        className="split-value"
                                                        value={split.split3Percent}
                                                        onChange={(v) => {
                                                            this.updateBillSplit(
                                                                index,
                                                                "split3Percent",
                                                                Number(v)
                                                            );
                                                        }}
                                                        placeholderText={formatMessage(messages.s3)}
                                                        labelTitle={
                                                            this.props.agvanceFieldData
                                                                ?.s3SplitArrangement
                                                                ? this.props.agvanceFieldData
                                                                      ?.s3SplitArrangement
                                                                : formatMessage(messages.s3)
                                                        }
                                                        useRawTitle={false}
                                                        useValueTitle={true}
                                                        testId={`field-info-bill-split3-percent-input-${index}`}
                                                    />
                                                    {index !== 0 &&
                                                        !this.disableBillSplitsEditing() && (
                                                            <div
                                                                className="trashcan-icon-container"
                                                                onClick={() => {
                                                                    if (split.splitCustomerID) {
                                                                        this.setState({
                                                                            billSplitIndexToDelete:
                                                                                index,
                                                                            showBillSplitsDeleteDialog:
                                                                                true,
                                                                        });
                                                                    } else {
                                                                        this.removeBillSplit(index);
                                                                    }
                                                                }}
                                                            >
                                                                <TrashcanIcon className="trashcan-icon" />
                                                            </div>
                                                        )}
                                                </div>
                                            </React.Fragment>
                                        );
                                    })}
                            </div>
                            {!this.disableBillSplitsEditing() && (
                                <NoLink
                                    className={classNames(
                                        "listAddNoLink",
                                        !this.validSplitBilling() ? "disabled" : null
                                    )}
                                    label={formatMessage(messages.addAgvanceCustomerSplitText)}
                                    onClick={() => {
                                        if (this.validSplitBilling()) {
                                            this.addBillSplit();
                                        }
                                    }}
                                />
                            )}
                        </Bucket>
                    )}
                </div>
                <DialogBox
                    isOpen={this.state.showCertOrganicChangeDialog}
                    onAction={() => {
                        this.updateFieldModel("certifiedOrganic", this.state.newCertOrganicValue);
                        this.closeCertOrganicVerifyModal();
                    }}
                    onClose={() => this.closeCertOrganicVerifyModal()}
                    footerType={DialogBoxFooterType.YES_NO}
                    title={formatMessage(messages.certOrganicVerifyTitle)}
                    testId="field-info-cert-organic-verify-modal"
                >
                    {formatMessage(messages.certOrganicVerifyMessage)}
                </DialogBox>
                <DialogBox
                    isOpen={this.state.showFarmSplitConfirmDialog}
                    onAction={() => this.addFarmSplitsToField()}
                    onClose={() => this.closeFarmSplitConfirmModal()}
                    footerType={DialogBoxFooterType.YES_NO}
                    title={formatMessage(messages.certOrganicVerifyTitle)}
                    testId="field-info-farm-split-confirm-modal"
                >
                    {formatMessage(messages.addFarmSplitsVerifyMessage)}
                </DialogBox>
                <DialogBox
                    isOpen={this.state.showBillSplitsDeleteDialog}
                    onAction={() => this.removeBillSplit(this.state.billSplitIndexToDelete)}
                    onClose={() => this.setState({ showBillSplitsDeleteDialog: false })}
                    footerType={DialogBoxFooterType.YES_NO}
                    title={formatMessage(messages.certOrganicVerifyTitle)}
                    testId="field-info-bill-split-delete-modal"
                >
                    {formatMessage(messages.billSplitDeleteConfirmMessage)}
                </DialogBox>
                <FieldBoundaryTransfer />
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const picklistData = picklistSelectors.getPicklistState(state);
    const unitData = unitSelectors.getUnitsState(state);
    const dropdowns = getFieldModuleDropdowns(state);

    let pickListData = {},
        unitTypeData,
        dropDownData = {};
    for (const key of Object.keys(pickLists)) {
        pickListData = Object.assign(pickListData, {
            [key]: picklistData[pickLists[key]],
        });
    }
    for (const key of Object.keys(units)) {
        unitTypeData = Object.assign(unitData, { [key]: unitData[units[key]] });
    }
    if (dropdowns) {
        for (const key of Object.keys(dropdowns)) {
            dropDownData = Object.assign(dropDownData, {
                [key]: dropdowns[key],
            });
        }
    }

    return {
        ...pickListData,
        ...unitTypeData,
        ...dropDownData,
        agvanceCustomerList: selectors.getAgvanceCustomers(state),
        agvanceFarmSplits: getAgvanceFarmSplits(state),
        agvanceFieldData: getAgvanceFieldData(state),
        agvanceFieldList: getAgvanceFieldList(state),
        agvanceOrgList: getAgvanceOrgLevelList(state),
        agvanceFieldClassificationList: getAgvanceFieldClassificationList(state),
        agvanceLocationsList: getAgvanceLocationsList(state),
        customerSearchIsLoading: selectors.getCustomerSearchIsLoading(state),
        errorCodes: getErrorCodes(state),
        farmNames: getFarmNames(state),
        fetchingFieldInfo: getFetchingFieldInfo(state),
        isAdditionalAgvanceCustomerSearch: selectors.getAdditionalAgvanceCustomerSearchFlag(state),
        isAgvanceCustomerListOutOfBound: selectors.getAgvanceCustomerListOutOfBound(state),
        personalityId: getTheUserPersonalityId(state),
        userInfo: getUser(state),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        refreshDropdowns: (dropdowns) => {
            dispatch(fetchDropdownData({ ...dropdowns, action: fetchedDropdownData }));
        },
        onFetchAgvanceCustomer: (payload) =>
            dispatch(fetchAgvanceCustomer({ orgLevelGuidList: payload, filterData: 2 })),
        onFetchAgvanceCustomerWithNameSearch: (payload) =>
            dispatch(fetchAgvanceCustomerWithNameSearch({ ...payload, filterData: 2 })),
        fetchAgvanceFarmSplits: (payload) => dispatch(actions.fetchAgvanceFarmSplits(payload)),
        fetchAgvanceFieldList: (payload) => dispatch(actions.fetchAgvanceFieldList(payload)),
        fetchAgvanceFieldClassificationList: (payload) =>
            dispatch(actions.fetchAgvanceFieldClassificationList(payload)),
        fetchAgvanceLocationsList: (payload) =>
            dispatch(actions.fetchAgvanceLocationsList(payload)),
        fetchAgvanceOrgLevelList: (payload) => dispatch(actions.fetchAgvanceOrgLevelList(payload)),
        fetchFarmNames: (payload) => dispatch(actions.fetchFarmNames(payload)),
        resetAgvanceCustomerList: () => dispatch(resetAgvanceCustomers()),
        clearAgvanceCustomerList: () => dispatch(clearAgvanceCustomerList()),
        clearAgvanceFarmSplits: () => dispatch(actions.clearAgvanceFarmSplits()),
        clearAgvanceFieldList: () => dispatch(actions.clearAgvanceFieldList()),
        clearFarmNames: () => dispatch(actions.setFarmNames([])),
        clearAgvanceFieldClassificationList: () =>
            dispatch(actions.clearAgvanceFieldClassificationList()),
        clearAgvanceLocationsList: () => dispatch(actions.fetchAgvanceLocationsListSucceeded([])),
        clearFieldsToMove: () => dispatch(actions.clearFieldsToMove()),
    };
};

// Cast to `any`: connect()'s TS inference over this file's dynamically-built mapStateToProps
// result collapses the connected component's prop type down to injectIntl's own type, rejecting
// any own props at all (including ones the class already required before this container existed).
const ConnectedFieldInformationDetail: any = connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(withApiResult(FieldInformationDetail)));

interface IFieldInformationDetailOwnProps {
    activeField: IFieldModel;
    customerGuid: string;
    editFieldGuid: string;
    hasActiveInactive: boolean;
    onInsuranceCropUnlinkedChange?: (hasUnlinkedCrop: boolean) => void;
    updateFieldModel: (model: any) => void;
}

const FieldInformationDetailContainer = (props: IFieldInformationDetailOwnProps): JSX.Element => {
    const { crops } = useCropList({ customerGuid: props.customerGuid });
    const cropData = useMemo(() => toCropDropdownOptions(crops), [crops]);

    return <ConnectedFieldInformationDetail {...props} cropData={cropData} />;
};

export default FieldInformationDetailContainer;
